import { useRouter } from 'next/router'
import useSWR from 'swr'

import { getCountryAndLocaleStrings } from '../../utils/locales'
import { buildStrapiUrl } from '../../utils/strapi'
import { strapiFetcher } from '../../utils/fetcher'
import useCategoryDataStructure from '../../components/hooks/useCategoryDataStructure'
import { StrapiDroppeAdCampaignAttribute, StrapiDroppeAdCampaignResp } from '../../utils/types/strapi'

interface UseDroppeAdCategoryBannerParams {
  location: 'category_sidebar' // More locations to be added
}

const useDroppeAdCategoryBanner = (
  params: UseDroppeAdCategoryBannerParams,
): StrapiDroppeAdCampaignAttribute | undefined => {
  const { location } = params
  const { locale: countryAndLocaleString } = useRouter()
  const { locale, country } = getCountryAndLocaleStrings(countryAndLocaleString)
  const { parentNodes } = useCategoryDataStructure()

  const apiUrl = buildStrapiUrl({
    endpoint: 'droppe-ad-campaigns',
    locale,
    queryParams: [
      `&filters[countries][countryCode][$in]=${country.toUpperCase()}`,
      // TODO: Uncomment when Strapi API filtering is fixed - github issue posted
      // '&filters[bannerCategoryPage][isActive][$eq]=true',
      // `&filters[bannerCategoryPage][targetCategory][code][$eq]=${currentCategory}`,
      // `&filters[bannerCategoryPage][location][$eq]=${location}`,
      '&populate[bannerCategoryPage][populate][0]=targetCategory,desktopBanner,mobileBanner',
    ].join(''),
  })

  const { data: strapiResp } = useSWR<StrapiDroppeAdCampaignResp>(
    [apiUrl, countryAndLocaleString],
    (url) => strapiFetcher(url, countryAndLocaleString),
  )

  return parentNodes.reverse().flatMap((category) => (
    strapiResp?.data.map((campaign) => {
      const bannerMatch = campaign
        .attributes?.bannerCategoryPage
        ?.find((bannerData) => (
          bannerData.isActive
          && bannerData.location === location
          && bannerData.targetCategory?.data?.attributes.code === category.code))
      return {
        ...campaign.attributes,
        bannerCategoryPage: bannerMatch ? [bannerMatch] : [],
      }
    }) || []
  )).find((campaign) => (campaign.bannerCategoryPage?.length || 0) > 0)
}

export default useDroppeAdCategoryBanner
